import {
  CandidateCategory,
  CandidateCautionReason,
  CandidateStatus,
} from "@/api";
import { type CandidateFull } from "@/models/candidate";
import { Candidates } from "@/models/candidates";
import type { LivePosition } from "@/models/position";
import { LivePositions } from "@/models/positions";
import type { State as RootState } from "@/stores";
import { type Module } from "vuex";

export interface DashboardData {
  positions: LivePositions;
  allCandidates: Candidates;
}

export interface State {
  positions: LivePositions;
  allCandidates: Candidates | null;
}

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    positions: new LivePositions(),
    allCandidates: null,
  },
  getters: {
    phoneScreeningCompletedCandidates(state): Candidates | null {
      if (state.allCandidates === null) return null;
      return new Candidates(
        state.allCandidates
          .sortBy({
            key: "callTime",
            direction: "desc",
          })
          .filter(
            (candidate) =>
              (candidate.category === CandidateCategory.RequestForApproval ||
                candidate.category === CandidateCategory.Unqualified) &&
              candidate.status !== CandidateStatus.Withdrawn &&
              !candidate.evaluation_shared_at &&
              !candidate.archived,
          ),
      );
    },
    incompleteInterviewCandidates(state): Candidates | null {
      if (state.allCandidates === null) return null;
      return new Candidates(
        state.allCandidates
          .sortBy({
            key: "lastMessage",
            direction: "desc",
          })
          .filter(
            (candidate) =>
              candidate.category === CandidateCategory.NeedFollowUp &&
              candidate.status !== CandidateStatus.Withdrawn &&
              candidate.caution_reason !==
                CandidateCautionReason.SchedulingNotResponding &&
              !candidate.archived,
          ),
      );
    },
    phoneScreeningsCompletedCount(_state, getters): number | null {
      if (getters.phoneScreeningCompletedCandidates === null) return null;
      return getters.phoneScreeningCompletedCandidates.length;
    },
  },
  actions: {
    async getDashboardData({ commit }) {
      const livePositions = (await this.dispatch(
        "position/getLivePositions",
      )) as LivePositions;

      const candidatesByPosition = await Promise.all(
        livePositions.map(async (position: LivePosition) => {
          const positionCandidates = (await this.dispatch(
            "candidate/getCandidates",
            position.id,
          )) as CandidateFull[];
          return new Candidates(positionCandidates);
        }),
      );

      const allCandidates = candidatesByPosition.reduce(
        (aggregatedCandidates, positionCandidates) =>
          aggregatedCandidates.append(positionCandidates),
        new Candidates(),
      );

      const dashboardData = {
        positions: livePositions,
        allCandidates,
      } as DashboardData;
      commit("setPositions", dashboardData.positions);
      commit("setAllCandidates", dashboardData.allCandidates);

      return dashboardData;
    },
    async setCandidates({ state, commit }, allCandidates: Candidates) {
      commit("setAllCandidates", allCandidates);

      const byPosition = new Map<number, CandidateFull[]>();
      for (const c of allCandidates) {
        const pid = c.position_id;
        if (pid != null) {
          if (!byPosition.has(pid)) byPosition.set(pid, []);
          byPosition.get(pid)!.push(c);
        }
      }
      for (const [positionId, candidates] of byPosition) {
        this.commit("candidate/setCandidates", {
          positionId,
          candidates: new Candidates(candidates),
        });
      }

      return { positions: state.positions, allCandidates } as DashboardData;
    },
    async updateCandidate({ state, dispatch }, candidate: CandidateFull) {
      if (state.allCandidates === null) {
        return dispatch("setCandidates", new Candidates([candidate]));
      }
      const nextAllCandidates = state.allCandidates.update(candidate);
      return dispatch("setCandidates", nextAllCandidates);
    },
    async removeCandidate({ state, dispatch }, candidate: CandidateFull) {
      if (state.allCandidates === null) return null;
      const nextAllCandidates = state.allCandidates.remove(candidate);
      return dispatch("setCandidates", nextAllCandidates);
    },
  },
  mutations: {
    setPositions(state, positions: LivePositions) {
      state.positions = positions;
    },
    setAllCandidates(state, allCandidates: Candidates | null) {
      state.allCandidates = allCandidates;
    },
  },
};
